'use client';

import { trackContentfulClick, trackPromoBannerCTAClick } from '@libs/analytics';
import { LayoutContainer, PromoBanner, type PromoBannerProps, TextPassage } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import { optimizeImage, propsImagesItems } from '../../utils/propsImagesItems';
import type { BannerProps } from './types';
import {
    NEW_TAB,
    PRIMARY_BUTTON_STYLE,
    SECONDARY_BUTTON_STYLE,
    TARGET_BLANK,
    TARGET_SELF,
    TEXT_ALIGNMENT_CENTER,
    TEXT_COLOR_WHITE,
} from '../../constants';
import './Banner.scss';
import type { BannerVariant } from '../../types';
import cn from 'classnames';

const BANNER_VARIANT_SIZE_MAP: Record<BannerVariant, PromoBannerProps['size']> = {
    Small: 'sm',
    Medium: 'md',
    Category: 'md-alt',
    Large: 'lg',
    'Landing Page': 'xlg',
};

export default function Banner({
    variant,
    header,
    description,
    textColor,
    textAlignment,
    image,
    cta,
    isH1,
    onClick,
}: BannerProps) {
    const pathname = usePathname();
    const { link, behavior, buttonStyle, text: buttonLabel } = cta || {};
    const optimizedImage = optimizeImage(image);
    /**
     * This component only works with deprecated screen sizes. :(
     */
    const imageItems = propsImagesItems(optimizedImage, true);
    const bannerSize = BANNER_VARIANT_SIZE_MAP[variant];
    const descriptionSize = variant === 'Landing Page' ? 'xlg' : undefined;
    const headerText = bannerSize !== 'sm' ? header : header?.slice(0, 75);
    const isDescriptionVisible = Boolean(description) && (bannerSize === 'lg' || bannerSize === 'xlg');

    const getTarget = () => {
        if (!behavior) {
            return undefined;
        }

        return behavior === NEW_TAB ? TARGET_BLANK : TARGET_SELF;
    };

    const getButtonStyle = () => {
        if (!buttonStyle) {
            return undefined;
        }

        return buttonStyle?.toLowerCase() === PRIMARY_BUTTON_STYLE ? PRIMARY_BUTTON_STYLE : SECONDARY_BUTTON_STYLE;
    };

    const showCTA = !!cta && bannerSize !== 'md-alt' && bannerSize !== 'xlg';
    const linkText = cn(headerText, isDescriptionVisible && description, showCTA && cta.text);

    return (
        <LayoutContainer className="banner-container">
            <PromoBanner
                data-testid="sparky-banner-test-id"
                title={headerText}
                imageItems={imageItems}
                imgSrc={imageItems[0]?.srcSet}
                imgAlt={image.webAltText}
                cta={showCTA}
                onClick={() => {
                    trackPromoBannerCTAClick(header ?? '', variant, buttonLabel ?? '');
                    trackContentfulClick({
                        linkText,
                        linkUrl: link?.webUrl ?? '',
                        promoTitle: headerText ?? '',
                        promoVariant: variant,
                        promoOrigin: pathname,
                        promoDesc: headerText ?? '',
                        promoType: 'banner-container',
                    });
                    onClick?.();
                }}
                headingTagName={isH1 ? 'h1' : 'h2'}
                href={link?.webUrl || undefined}
                target={getTarget()}
                linkLabel={buttonLabel ?? undefined}
                buttonVariant={getButtonStyle()}
                size={bannerSize}
                inverted={textColor ? textColor.toLowerCase() === TEXT_COLOR_WHITE : undefined}
                centerText={textAlignment ? textAlignment.toLowerCase() === TEXT_ALIGNMENT_CENTER : undefined}
                className="promo-banner"
            >
                {isDescriptionVisible && <TextPassage size={descriptionSize}>{description}</TextPassage>}
            </PromoBanner>
        </LayoutContainer>
    );
}
